import { Component, Input, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CommonService } from '../../services/common.service';
import { API_STATUS, ApiResponse, ERROR_RESPONSE, SearchConfig } from '../../model/api.model';
import { CommonUtils } from '../../utils/common.utils';
import { LoadingSkeletonComponent } from '../../layout/loading-skeleton/loading-skeleton.component';
import { CamelCasePipe } from '../../utils/camel-case.pipe';
import { DynamicFormArray } from '../../model/form.model';

export interface DynamicCardItem {
  title: string;
  value?: string | number;
  subtitle?: string;
}

export interface DynamicCardSchemaItem {
  label: string;
  subLabel: string;
  fieldKey: string;
  subtitleFieldKey?: string;
  suffix?: string;
  valueType?: 'number' | 'amount' | 'text';
  icon?: string;
}
export type DynamicCardFormat = 'default' | 'borderCard' | 'dealerPerformanceCard';

@Component({
  selector: 'app-dynamic-cards',
  standalone: true,
  imports: [CommonModule, LoadingSkeletonComponent, CamelCasePipe],
  templateUrl: './dynamic-cards.component.html',
  styleUrl: './dynamic-cards.component.scss',
})
export class DynamicCardsComponent implements OnDestroy {
  private readonly commonService = inject(CommonService);
  private _schema: DynamicCardSchemaItem[] = [];
  private _apiConfig?: SearchConfig;
  private _groupType: any;
  private readonly commonUtils = inject(CommonUtils);
  private patchScheduled = false;
  private inFlightRequestKey = '';
  private lastCompletedRequestKey = '';
  private activeRequestCount = 0;
  private _defaultLoader = false;
  private _filterSchema?: DynamicFormArray;
  private _globalFilterFields: string[] = [];
  private globalpayloadSubject: any;
  private globalPayload: any;
  @Input() requiredGlobalPayloadFields: string[] = [];
  @Input() dataTransform?: (data: Record<string, unknown>) => Record<string, unknown>;
  spinnerName = 'cards';
  isLoading = false;

  @Input() cards: DynamicCardItem[] = [];

  @Input()
  set schema(value: DynamicCardSchemaItem[] | null | undefined) {
    this._schema = Array.isArray(value) ? value : [];
    this.cards = this._schema.map((card) => ({
      title: card.label,
      subtitle: card.subLabel,
      value: card.suffix ? `0${card.suffix}` : 0,
    }));
    this.triggerPatch();
  }
  get schema(): DynamicCardSchemaItem[] {
    return this._schema;
  }

  @Input()
  set apiConfig(value: SearchConfig | undefined) {
    this._apiConfig = value;
    this.triggerPatch();
  }
  get apiConfig(): SearchConfig | undefined {
    return this._apiConfig;
  }
  @Input() columns = 1;
  @Input() rows = 1;
  @Input() minCardWidth = '160px';
  @Input() onLoadFetch = true;
  @Input()
  set filterSchema(value: DynamicFormArray | undefined) {
    this._filterSchema = value;
    this.subscribeToGlobalPayload();
  }
  get filterSchema(): DynamicFormArray | undefined {
    return this._filterSchema;
  }
  @Input()
  set globalFilterFields(value: string[] | undefined) {
    this._globalFilterFields = value ?? [];
    this.subscribeToGlobalPayload();
  }
  get globalFilterFields(): string[] {
    return this._globalFilterFields;
  }
  @Input()
  set defaultLoader(value: boolean) {
    this._defaultLoader = !!value;
    this.triggerPatch();
  }
  get defaultLoader(): boolean {
    return this._defaultLoader;
  }
  @Input() format: DynamicCardFormat = 'default';
  @Input() showIcons = false;
  @Input()
  set elementId(value: any) {
    this.spinnerName = String(value ?? 'cards');
  }

  @Input()
  set groupType(value: any) {
    this._groupType = value;
    this.triggerPatch();
  }
  get groupType(): any {
    return this._groupType;
  }

  private toNumber(value: unknown): number {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  ngOnDestroy(): void {
    this.globalpayloadSubject?.unsubscribe?.();
  }

  private subscribeToGlobalPayload(): void {
    this.globalpayloadSubject?.unsubscribe?.();
    this.globalpayloadSubject = undefined;
    if (!this._filterSchema?.globalPayloadSubject) {
      return;
    }
    this.globalpayloadSubject = this._filterSchema.globalPayloadSubject.subscribe((globalPayload: any) => {
      if (!this._globalFilterFields.includes(globalPayload?.FieldName)) {
        return;
      }
      this.globalPayload = structuredClone(globalPayload?.Value?.body);
      this.triggerPatch();
    });
  }

  private triggerPatch(): void {
    if (!this._schema.length || !this._apiConfig?.urlPath || !this._apiConfig?.path) {
      return;
    }

    if (!this.onLoadFetch && !this.hasGroupTypeTrigger() && !this.hasGlobalPayloadTrigger()) {
      if (this._defaultLoader) {
        this.isLoading = true;
      }
      return;
    }

    if (this.patchScheduled) {
      return;
    }

    this.patchScheduled = true;
    queueMicrotask(() => {
      this.patchScheduled = false;
      void this.patchCardsFromApi();
    });
  }

  private patchCardsFromApi(): Promise<void> {
    if (!this.onLoadFetch && !this.hasGroupTypeTrigger() && !this.hasGlobalPayloadTrigger()) {
      return Promise.resolve();
    }
    if (!this._apiConfig?.urlPath || !this._apiConfig?.path) {
      return Promise.resolve();
    }
    if (!this.hasRequiredGlobalPayload()) {
      this.isLoading = !!this._defaultLoader;
      return Promise.resolve();
    }

    const requestKey = this.buildRequestKey();
    if (this.inFlightRequestKey === requestKey || this.lastCompletedRequestKey === requestKey) {
      return Promise.resolve();
    }

    this.inFlightRequestKey = requestKey;
    this.activeRequestCount += 1;
    this.isLoading = true;
    this.commonUtils.spinner('show', this.spinnerName as any);

    const fetchParams = {
      ...structuredClone(this._apiConfig),
      body: {
        ...(this._apiConfig.body ),
        ...(this.globalPayload ),
      },
    };
    const urlPath = this._apiConfig.urlPath;

    return this.commonService.getDataFromDataSource(fetchParams, urlPath).then((response) => {
      if (this.isApiSuccess(response)) {
        const data = this.extractApiData(response);
        if (!data) {
          return;
        }
        this.patchData(this.dataTransform ? this.dataTransform(data) : data);
        this.lastCompletedRequestKey = requestKey;
      } else {
          this.commonUtils.showMatSnackBar(ERROR_RESPONSE['noDataFound']);
        }
    }).catch((err) => {
      console.info('Error fetching dynamic cards data', err); 
    }).finally(() => {
      this.activeRequestCount = Math.max(0, this.activeRequestCount - 1);
      if (this.activeRequestCount === 0) {
        this.isLoading = false;
        this.commonUtils.spinner('hide', this.spinnerName as any);
      }
      if (this.inFlightRequestKey === requestKey) {
        this.inFlightRequestKey = '';
      }
    });
  }

  private buildRequestKey(): string {
    const schemaKeys = this._schema.map((item) => item.fieldKey).join('|');
    const body = { ...(this._apiConfig?.body ), ...(this.globalPayload ) };
    const method = this._apiConfig?.method ?? '';
    const path = this._apiConfig?.path ?? '';
    const urlPath = this._apiConfig?.urlPath ?? '';
    return `${method}|${urlPath}|${path}|${JSON.stringify(body)}|${String(this._groupType)}|${schemaKeys}`;
  }

  private hasGroupTypeTrigger(): boolean {
    return !(this._groupType === undefined || this._groupType === null || this._groupType === '');
  }

  private hasGlobalPayloadTrigger(): boolean {
    return !!this.globalPayload && Object.keys(this.globalPayload).length > 0;
  }

  private hasRequiredGlobalPayload(): boolean {
    if (!this.requiredGlobalPayloadFields?.length) {
      return true;
    }
    return this.requiredGlobalPayloadFields.every((key) => {
      const value = this.globalPayload?.[key] ?? this._apiConfig?.body?.[key];
      return value !== '' && value !== null && value !== undefined;
    });
  }

  private isApiSuccess(response: ApiResponse): boolean {
    const extendedResponse = response as ApiResponse & { statusCode?: number };
    return response.status === API_STATUS['Success'] || extendedResponse.statusCode === 200;
  }

  private extractApiData(response: ApiResponse): Record<string, unknown> | undefined {
    const extendedResponse = response as ApiResponse & { payload?: unknown; result?: unknown };
    const source = extendedResponse.payload ?? extendedResponse.result;

    if (Array.isArray(source)) {
      const firstItem = source[0] as unknown;
      return this.extractRecord(firstItem);
    }

    return this.extractRecord(source);
  }

  private extractRecord(source: unknown): Record<string, unknown> | undefined {
    if (!source || typeof source !== 'object') {
      return undefined;
    }

    const record = source as Record<string, unknown>;
    const nestedPayload = record['payload'] ?? record['result'];

    if (nestedPayload !== undefined) {
      if (Array.isArray(nestedPayload)) {
        return this.extractRecord(nestedPayload[0]);
      }
      return this.extractRecord(nestedPayload) ?? (nestedPayload as Record<string, unknown>);
    }

    return record;
  }

  private patchData(source: Record<string, unknown> | null | undefined): void {
    const safeSource = source ?? {};
    this.cards = this._schema.map((card) => {
      const numericValue = this.toNumber(safeSource[card.fieldKey]);
      const value = card.suffix ? `${numericValue}${card.suffix}` : numericValue;

      return {
        title: card.label,
        subtitle: card.subtitleFieldKey && safeSource[card.subtitleFieldKey] !== undefined
          ? String(safeSource[card.subtitleFieldKey] ?? '')
          : card.subLabel,
        value,
      };
    });
  }

  getCardClass(idx: number): string {
    if (this.format === 'dealerPerformanceCard') {
      return `card--dealer-performance card--dealer-performance-${idx % 8}`;
    }

    if (this.format !== 'borderCard') {
      return '';
    }

    const classes = ['card--border-card'];
    const variantByIndex = ['card--eb-total', 'card--eb-amount', 'card--eb-attended', 'card--eb-noshow'];
    classes.push(variantByIndex[idx % variantByIndex.length]);
    return classes.join(' ');
  }

  getCardIcon(idx: number): string {
    return this.schema[idx]?.icon ?? '';
  }

  get resolvedColumns(): number {
    return Math.max(1, this.columns || 1);
  }

  get resolvedRows(): number {
    return Math.max(1, this.rows || 1);
  }

  get visibleCards(): DynamicCardItem[] {
    const maxItems = this.resolvedColumns * this.resolvedRows;
    return this.cards.slice(0, maxItems);
  }

  get gridTemplateColumns(): string {
    return `repeat(${this.resolvedColumns}, minmax(${this.minCardWidth}, 1fr))`;
  }
}
